
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Building, CertificationStatus } from '@/types/portfolio';
import { toast } from 'sonner';

export const useBuildings = (portfolioId: string) => {
  const queryClient = useQueryClient();
  
  const {
    data: buildings,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['buildings', portfolioId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('buildings')
        .select('*')
        .eq('portfolio_id', portfolioId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Building[];
    },
    enabled: !!portfolioId
  });

  const createBuilding = useMutation({
    mutationFn: async (building: { name: string; description: string | null; address?: string }) => {
      const { data, error } = await supabase
        .from('buildings')
        .insert({
          portfolio_id: portfolioId,
          name: building.name,
          description: building.description,
          address: building.address || null,
          certification_status: 'pending' as CertificationStatus
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as Building;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buildings', portfolioId] });
      toast.success('Building added successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error adding building: ${error.message}`);
    }
  });

  const deleteBuilding = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('buildings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buildings', portfolioId] });
      toast.success('Building removed successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error removing building: ${error.message}`);
    }
  });

  return {
    buildings,
    isLoading,
    error,
    refetch,
    createBuilding,
    deleteBuilding
  };
};
